import api, { BASE_URL } from './base';
import { useAuthStore } from '@/stores/authStore';
import type {
  ChatbotMessageRequest,
  ChatbotMessageResponse,
  ChatbotContextResponse,
  ChatbotContextClearResponse,
  ChatbotHealthResponse,
  ChatbotErrorResponse,
  ChatbotAudioResponse,
  ChatbotConfiguration,
  ChatbotConfigurationUpdate,
  ChatbotFeedbackRequest,
  ChatbotFeedbackResponse,
  ChatbotFileUploadResponse,
  ChatbotFilesListResponse,
  ChatbotFileDeleteResponse,
  ChatbotUploadedFile,
  StreamEvent
} from '@/types/chatbot';


const CHATBOT_BASE_URL = '/api/v1/chatbot';

/**
 * Send a message to the chatbot and get a response
 */
export const sendChatMessage = async (
  message: string
): Promise<ChatbotMessageResponse> => {
  try {
    const request: ChatbotMessageRequest = { message };
    const response = await api.post<ChatbotMessageResponse>(
      `${CHATBOT_BASE_URL}/chat/`,
      request
    );
    return response.data;
  } catch (error: any) {
    if (error.response?.data) {
      const errorData = error.response.data as ChatbotErrorResponse;
      throw new Error(errorData.message || errorData.error);
    }
    throw new Error('Failed to send message to chatbot');
  }
};

/**
 * Send a message to the chatbot with streaming response using fetch and ReadableStream
 * Now supports: ping, tool_start, tool_end, content, done events
 */
export const sendChatMessageStream = async (
  message: string,
  onMessage: (data: any) => void,
  onError?: (error: Error) => void,
  onComplete?: () => void,
  onDone?: () => void,
  onToolStart?: (identifier: string, message: string) => void,
  onToolEnd?: (identifier: string) => void
): Promise<{ abort: () => void }> => {
  const controller = new AbortController();
  const token = useAuthStore.getState().auth.accessToken;
  
  try {
    const response = await fetch(`${BASE_URL}${CHATBOT_BASE_URL}/chat/stream/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': token ? `Bearer ${token}` : '',
      },
      body: JSON.stringify({ message }),
      signal: controller.signal,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const reader = response.body?.getReader();
    if (!reader) {
      throw new Error('No readable stream available');
    }

    const decoder = new TextDecoder();
    let buffer = '';

    const processStream = async () => {
      let isCompleteCalled = false;
      const finalize = () => {
        if (!isCompleteCalled) {
          isCompleteCalled = true;
          onComplete?.();
        }
      };

      const processLine = (line: string): boolean => {
        if (line.startsWith('data: ')) {
          const data = line.slice(6).trim();
          
          if (data === '[DONE]') {
            onDone?.();
            finalize();
            return true;
          }
          
          if (!data) return false;

          try {
            const event: StreamEvent = JSON.parse(data);
            
            switch (event.type) {
              case 'ping':
                // Keep-alive ping, ignore
                break;
                
              case 'tool_start': {
                const identifier = event.tool_name ?? event.tool ?? event.message ?? '';
                const detail = event.message ?? event.tool ?? '';
                if (identifier || detail) {
                  onToolStart?.(identifier || detail, detail || identifier);
                }
                break;
              }
                
              case 'tool_end': {
                const identifier = event.tool_name ?? event.tool ?? event.message ?? '';
                if (identifier) {
                  onToolEnd?.(identifier);
                }
                break;
              }
                
              case 'content':
                {
                  const textPayload = event.text ?? event.delta ?? event.content ?? event.data;
                  if (typeof textPayload === 'string' && textPayload.length > 0) {
                    onMessage({ content: textPayload });
                  }
                }
                break;
                
              case 'done':
                {
                  const finalPayload = event.text ?? event.delta ?? event.content ?? event.data;
                  if (typeof finalPayload === 'string' && finalPayload.length > 0) {
                    onMessage({ content: finalPayload });
                  }
                  onDone?.();
                }
                finalize();
                return true;
                
              default:
                // Fallback for legacy format or unknown types
                const legacyEvent = event as any;
                if (legacyEvent.content && typeof legacyEvent.content === 'string' && legacyEvent.content.length > 0) {
                  onMessage(legacyEvent);
                } else if (event.text && typeof event.text === 'string' && event.text.length > 0) {
                  onMessage({ content: event.text });
                } else if (legacyEvent.delta && typeof legacyEvent.delta === 'string' && legacyEvent.delta.length > 0) {
                  onMessage({ content: legacyEvent.delta });
                }
            }
          } catch (parseError) {
            // If JSON parsing fails, treat as plain text content
            console.warn('Failed to parse streaming event:', data);
            if (data.trim()) {
              onMessage({ content: data });
            }
          }
        }
        return false;
      };

      try {
        while (true) {
          const { done, value } = await reader.read();
          
          if (done) {
            if (buffer) {
              const lines = buffer.split('\n');
              for (const line of lines) {
                if (processLine(line)) return;
              }
            }
            finalize();
            break;
          }

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';

          for (const line of lines) {
            if (processLine(line)) return;
          }
        }
      } catch (error) {
        if (error instanceof Error && error.name !== 'AbortError') {
          onError?.(error);
        }
      } finally {
        reader.releaseLock();
        finalize();
      }
    };

    processStream().catch((error) => {
      if (error instanceof Error && error.name !== 'AbortError') {
        onError?.(error);
      }
    });
    
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') {
      onComplete?.();
    } else {
      onError?.(error as Error);
      onComplete?.();
    }
  }

  return {
    abort: () => controller.abort()
  };
};

/**
 * Get the current conversation context
 */
export const getChatbotContext = async (): Promise<ChatbotContextResponse> => {
  try {
    const response = await api.get<ChatbotContextResponse>(
      `${CHATBOT_BASE_URL}/context/`
    );
    return response.data;
  } catch (error: any) {
    if (error.response?.data) {
      const errorData = error.response.data as ChatbotErrorResponse;
      throw new Error(errorData.message || errorData.error);
    }
    throw new Error('Failed to get chatbot context');
  }
};

/**
 * Clear the conversation context
 */
export const clearChatbotContext = async (): Promise<ChatbotContextClearResponse> => {
  try {
    const response = await api.delete<ChatbotContextClearResponse>(
      `${CHATBOT_BASE_URL}/context/`
    );
    return response.data;
  } catch (error: any) {
    if (error.response?.data) {
      const errorData = error.response.data as ChatbotErrorResponse;
      throw new Error(errorData.message || errorData.error);
    }
    throw new Error('Failed to clear chatbot context');
  }
};

/**
 * Check chatbot health status
 */
export const getChatbotHealth = async (): Promise<ChatbotHealthResponse> => {
  try {
    const response = await api.get<ChatbotHealthResponse>(
      `${CHATBOT_BASE_URL}/health/`
    );
    return response.data;
  } catch (error: any) {
    if (error.response?.data) {
      const errorData = error.response.data as ChatbotErrorResponse;
      throw new Error(errorData.message || errorData.error);
    }
    throw new Error('Failed to check chatbot health');
  }
};

/**
 * Send an audio file to the chatbot for transcription and response
 */
export const sendAudioMessage = async (
  audioFile: File
): Promise<ChatbotAudioResponse> => {
  try {
    // Validate file size (25MB limit)
    const maxSize = 25 * 1024 * 1024; // 25MB in bytes
    if (audioFile.size > maxSize) {
      throw new Error('Le fichier audio est trop volumineux. Taille maximale : 25MB');
    }

    // Validate file type - match backend requirements exactly
    const fileType = audioFile.type.toLowerCase();
    const supportedTypes = [
      'audio/wav',
      'audio/ogg',
      'audio/mp3',
      'audio/mpeg'
    ];
    
    // Check if the base type (before semicolon) is supported
    const baseType = fileType.split(';')[0];
    const isSupported = supportedTypes.includes(baseType) || 
                       fileType.startsWith('audio/ogg') ||
                       fileType.startsWith('audio/wav') ||
                       fileType.startsWith('audio/mp3') ||
                       fileType.startsWith('audio/mpeg');
    
    if (!isSupported) {
      throw new Error('Format audio non supporté. Formats acceptés par le serveur : WAV, OGG, MP3, MPEG');
    }

    const formData = new FormData();
    formData.append('audio_file', audioFile);

    const response = await api.post<ChatbotAudioResponse>(
      `${CHATBOT_BASE_URL}/audio/`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  } catch (error: any) {
    if (error.response?.data) {
      const errorData = error.response.data as ChatbotErrorResponse;
      throw new Error(errorData.message || errorData.error);
    }
    throw new Error(error.message || 'Failed to send audio to chatbot');
  }
};

/**
 * Utility function to check if chatbot is available
 */
export const isChatbotAvailable = async (): Promise<boolean> => {
  try {
    const health = await getChatbotHealth();
    return health.status === 'healthy';
  } catch {
    return false;
  }
};

// Chatbot Configuration API (Super Admin)
const CHATBOT_CONFIG_BASE_URL = '/api/v1/super-admin/chatbot-config';

/**
 * Get the current chatbot configuration
 */
export const getChatbotConfiguration = async (): Promise<ChatbotConfiguration> => {
  try {
    const response = await api.get<ChatbotConfiguration>(
      `${CHATBOT_CONFIG_BASE_URL}/`
    );
    return response.data;
  } catch (error: any) {
    if (error.response?.data) {
      const errorData = error.response.data as ChatbotErrorResponse;
      throw new Error(errorData.message || errorData.error);
    }
    throw new Error('Failed to get chatbot configuration');
  }
};

/**
 * Update the chatbot configuration
 */
export const updateChatbotConfiguration = async (
  config: ChatbotConfigurationUpdate
): Promise<ChatbotConfiguration> => {
  try {
    const response = await api.put<ChatbotConfiguration>(
      `${CHATBOT_CONFIG_BASE_URL}/`,
      config
    );
    return response.data;
  } catch (error: any) {
    if (error.response?.data) {
      const errorData = error.response.data as ChatbotErrorResponse;
      throw new Error(errorData.message || errorData.error);
    }
    throw new Error('Failed to update chatbot configuration');
  }
};

/**
 * Partially update the chatbot configuration
 */
export const patchChatbotConfiguration = async (
  config: Partial<ChatbotConfigurationUpdate>
): Promise<ChatbotConfiguration> => {
  try {
    const response = await api.patch<ChatbotConfiguration>(
      `${CHATBOT_CONFIG_BASE_URL}/`,
      config
    );
    return response.data;
  } catch (error: any) {
    if (error.response?.data) {
      const errorData = error.response.data as ChatbotErrorResponse;
      throw new Error(errorData.message || errorData.error);
    }
    throw new Error('Failed to patch chatbot configuration');
  }
};

/**
 * Reset the chatbot configuration to default values
 */
export const resetChatbotConfiguration = async (): Promise<ChatbotConfiguration> => {
  try {
    const response = await api.post<ChatbotConfiguration>(
      `${CHATBOT_CONFIG_BASE_URL}/reset/`
    );
    return response.data;
  } catch (error: any) {
    if (error.response?.data) {
      const errorData = error.response.data as ChatbotErrorResponse;
      throw new Error(errorData.message || errorData.error);
    }
    throw new Error('Failed to reset chatbot configuration');
  }
};

// Feedback API Functions
const FEEDBACK_BASE_URL = '/api/v1/chatbot/feedback';

/**
 * Submit feedback for a chatbot interaction
 */
export const submitChatbotFeedback = async (
  feedbackData: ChatbotFeedbackRequest
): Promise<ChatbotFeedbackResponse> => {
  try {
    const response = await api.post<ChatbotFeedbackResponse>(
      `${FEEDBACK_BASE_URL}/`,
      feedbackData
    );
    return response.data;
  } catch (error: any) {
    if (error.response?.data) {
      const errorData = error.response.data as ChatbotErrorResponse;
      throw new Error(errorData.message || errorData.error);
    }
    throw new Error('Failed to submit feedback');
  }
};

/**
 * Get feedback history for the current user
 */
export const getChatbotFeedbackHistory = async (): Promise<ChatbotFeedbackResponse[]> => {
  try {
    const response = await api.get<ChatbotFeedbackResponse[]>(
      `${FEEDBACK_BASE_URL}/`
    );
    return response.data;
  } catch (error: any) {
    if (error.response?.data) {
      const errorData = error.response.data as ChatbotErrorResponse;
      throw new Error(errorData.message || errorData.error);
    }
    throw new Error('Failed to get feedback history');
  }
};

// File Upload API Functions
const FILE_UPLOAD_BASE_URL = '/api/v1/chatbot';

// Allowed file types and size limits
const ALLOWED_FILE_TYPES = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
  'text/plain',
  'image/png',
  'image/jpeg',
  'image/jpg'
];
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const MAX_FILES = 5;

/**
 * Upload a file to the chatbot for RAG analysis
 */
export const uploadChatbotFile = async (
  file: File
): Promise<ChatbotFileUploadResponse> => {
  try {
    // Validate file size
    if (file.size > MAX_FILE_SIZE) {
      throw new Error('File size exceeds 10MB limit');
    }

    // Validate file type
    const fileType = file.type.toLowerCase();
    const isValidType = ALLOWED_FILE_TYPES.some(type => fileType === type || fileType.startsWith(type.split('/')[0] + '/'));
    
    if (!isValidType && !file.name.match(/\.(pdf|docx|txt|png|jpe?g)$/i)) {
      throw new Error('Unsupported file type. Allowed: PDF, DOCX, TXT, PNG, JPEG');
    }

    const formData = new FormData();
    formData.append('file', file);

    const response = await api.post<ChatbotFileUploadResponse>(
      `${FILE_UPLOAD_BASE_URL}/upload/`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    
    // Normalize response: backend might return 'id' instead of 'file_id'
    const data = response.data;
    if (!data.file_id && (data as any).id) {
      data.file_id = (data as any).id;
    }
    
    return data;
  } catch (error: any) {
    if (error.response?.data) {
      const errorData = error.response.data as ChatbotErrorResponse;
      throw new Error(errorData.message || errorData.error);
    }
    throw new Error(error.message || 'Failed to upload file');
  }
};

/**
 * Get list of uploaded files for the current user
 */
export const getChatbotFiles = async (): Promise<ChatbotUploadedFile[]> => {
  try {
    const response = await api.get<ChatbotFilesListResponse | ChatbotUploadedFile[]>(
      `${FILE_UPLOAD_BASE_URL}/files/`
    );
    
    let files: ChatbotUploadedFile[] = [];
    
    // Handle both array response and object with files property
    if (Array.isArray(response.data)) {
      files = response.data;
    } else {
      files = response.data.files || [];
    }

    // Normalize files: backend might use 'id' instead of 'file_id'
    return files.map(file => {
      if (!file.file_id && (file as any).id) {
        return { ...file, file_id: (file as any).id };
      }
      return file;
    });
  } catch (error: any) {

    if (error.response?.data) {
      const errorData = error.response.data as ChatbotErrorResponse;
      throw new Error(errorData.message || errorData.error);
    }
    throw new Error('Failed to get uploaded files');
  }
};

/**
 * Delete an uploaded file
 */
export const deleteChatbotFile = async (
  fileId: string
): Promise<ChatbotFileDeleteResponse> => {
  try {
    if (!fileId) {
      throw new Error('File ID is required');
    }

    const response = await api.delete<ChatbotFileDeleteResponse>(
      `${FILE_UPLOAD_BASE_URL}/files/`,
      {
        params: { file_id: fileId }
      }
    );
    return response.data;
  } catch (error: any) {
    if (error.response?.data) {
      const errorData = error.response.data as ChatbotErrorResponse;
      throw new Error(errorData.message || errorData.error);
    }
    throw new Error('Failed to delete file');
  }
};

// Export constants for use in UI
export const CHATBOT_FILE_LIMITS = {
  MAX_FILE_SIZE,
  MAX_FILES,
  ALLOWED_FILE_TYPES,
  ALLOWED_EXTENSIONS: ['pdf', 'docx', 'txt', 'png', 'jpeg', 'jpg']
};